import { siteApiSlice } from "../../../app/siteApiSlice";
import { PeerReviewViewModel } from "../../peerReviews/peerReviewsApiSlice";
import { WritingAnalysisViewModel } from "../../scoreReport/scoreReportApiSlice";
import { StimulusMaterialViewModel } from "../../stimulusMaterial/stimulusMaterialApiSlice";
import { OrganizerViewModel } from "../organizing/organizingApiSlice";

export enum ContentFormat {
	Text = "Text",
	Html = "Html",
	Stf = "Stf",
}

export interface CreateEssayModel {
	promptId: number;
}

export interface CreateEssayResponse {
	essayId: number;
	draftId: number;
}

export interface ScoreCategoryModel {
	scoreCategoryId: number;
	name: string;
	description: string;
	order: number;
	shortName: string;
	isProctorScored: boolean;
	pegName: string;
	maxScore: number;
	codeName: string;
}

export interface CommentedEssayViewModel {
	id: number; // draftId
	showCommentToggler: boolean;
	showHighlightToggler: boolean;
	title: string;
	hasTitle: boolean;
	titleSpaceholder: boolean;
	showHighlights: boolean;
	responseFormat: ContentFormat;
	responseContent: string;
	responseMarkup: string;
	responseHighlights: string;
	responseComments: string;
	sourcesFormat: ContentFormat;
	sourcesContent: string;
	sourcesMarkup: string;
	sourcesHighlights: string;
	sourcesComments: string;
	writingAnalysis: WritingAnalysisViewModel;
	isPeerReview: boolean;
	hasCompletedPeerReviews: boolean;
	completedPeerReviewModels: PeerReviewViewModel[];
}

export interface WriteDraftViewModel {
	draftId: number;
	scoreCategories: ScoreCategoryModel[];
	commentedEssay: CommentedEssayViewModel;
	autosaveInterval: string;
	autosaveIntervalSec: number;
	bigChangeThreshold: number;
	organizer: OrganizerViewModel;
	promptHighlights: string;
	showSignLanguage: boolean;
	signLanguageVideoUrl: string;
	showMessages: boolean;
	allowPrewriting: boolean;
	isConstructedResponse: boolean;
	promptName: string;
	renderedPromptText: string;
	stimulusMaterials: StimulusMaterialViewModel[];
	isTimed: boolean;
	draftTitle: string;
	responseFormat: ContentFormat;
	responseContent: string;
	responseMarkup: string;
	responseHighlights: string;
	sourcesFormat: ContentFormat;
	sourcesContent: string;
	sourcesMarkup: string;
	sourcesHighlights: string;
	isFinalDraft: boolean;
	remainingTime: string;
	remainingTimeMs: number;
	allowCopyPaste: boolean;
	dueDateEnabled: boolean;
	dueDate: string | null;
	dueDateExceeded: boolean;
	plagiarismChecked: boolean;
}

export interface SaveDraftModel {
	draftId: number;
	title: string;
	responseContent: string;
	responseMarkup: string;
	responseHighlights: string;
	sourcesContent: string;
	sourcesMarkup: string;
	sourcesHighlights: string;
	promptHighlights: string;
}

export interface SaveDraftResponse {
	status: string;
	message?: string;
}

export interface SubmitDraftModel extends SaveDraftModel {
	force: boolean;
}

export interface SubmitDraftResponse extends SaveDraftResponse {
	force: boolean;
}

export interface PreviewViewModel {
	draftId: number;
	title: string;
	responseFormat: ContentFormat;
	responseContent: string;
	responseMarkup: string;
	sourcesFormat: ContentFormat;
	sourcesContent: string;
	sourcesMarkup: string;
	canFinalize: boolean;
	canRetract: boolean;
	createDate: string;
	writerName: string;
	courseName: string;
	promptTitle: string;
}

export const draftingApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		createEssay: build.mutation<CreateEssayResponse, CreateEssayModel>({
			query: (payload) => ({
				url: `/Draft/CreateEssay`,
				method: "POST",
				body: payload,
			}),
			invalidatesTags: ["Prompts", "WritingHistory"],
		}),
		writeDraft: build.query<WriteDraftViewModel, number>({
			query: (draftId) => ({
				url: `/Draft/Write`,
				params: { draftId },
			}),
		}),
		autosaveDraft: build.mutation<SaveDraftResponse, SaveDraftModel>({
			query: (payload) => ({
				url: `/Draft/AutosaveEssay`,
				method: "POST",
				body: payload,
			}),
			// don't invalidate tags when autosaving
		}),
		saveDraft: build.mutation<SaveDraftResponse, SaveDraftModel>({
			query: (payload) => ({
				url: `/Draft/AutosaveEssay`,
				method: "POST",
				body: payload,
			}),
			invalidatesTags: ["Prompts", "WritingHistory"],
		}),
		submitDraft: build.mutation<SubmitDraftResponse, SubmitDraftModel>({
			query: (payload) => ({
				url: `/Draft/Submit`,
				method: "POST",
				body: payload,
			}),
			invalidatesTags: ["Prompts", "WritingHistory", "ScoreReport"],
		}),
		quitAfterSavingDraft: build.mutation<void, number>({
			query: (draftId) => ({
				url: `Draft/QuitAfterSaving`,
				params: { draftId },
				method: "GET",
			}),
			invalidatesTags: ["Prompts", "WritingHistory"],
		}),
		reviseDraft: build.mutation<{ draftId: number }, number>({
			query: (draftId) => ({
				url: `/Draft/Revise`,
				params: { draftId },
				method: "POST",
			}),
			invalidatesTags: ["Prompts", "WritingHistory"],
		}),
		previewDraft: build.query<PreviewViewModel, number>({
			query: (draftId) => ({
				url: `/Draft/Preview`,
				params: { draftId },
			}),
			providesTags: ["WritingHistory"],
		}),
		finalizeDraft: build.mutation<void, number>({
			query: (draftId) => ({
				url: `/Draft/Finalize`,
				params: { draftId },
				method: "POST",
			}),
			invalidatesTags: ["Prompts", "WritingHistory", "ScoreReport"],
		}),
		undoFinalizeDraft: build.mutation<void, number>({
			query: (draftId) => ({
				url: `/Draft/ReturnToRevising`,
				params: { draftId },
				method: "POST",
			}),
			invalidatesTags: ["Prompts", "WritingHistory", "ScoreReport"],
		}),
		deleteDraft: build.mutation<void, number>({
			query: (draftId) => ({
				url: `/Draft/Delete`,
				params: { draftId },
				method: "POST",
			}),
			invalidatesTags: ["Prompts", "WritingHistory", "ScoreReport"],
		}),
	}),
});

export const {
	useCreateEssayMutation,
	useWriteDraftQuery,
	useLazyWriteDraftQuery,
	useAutosaveDraftMutation,
	useSaveDraftMutation,
	useSubmitDraftMutation,
	useQuitAfterSavingDraftMutation,
	useReviseDraftMutation,
	usePreviewDraftQuery,
	useFinalizeDraftMutation,
	useUndoFinalizeDraftMutation,
	useDeleteDraftMutation,
} = draftingApiSlice;
